// src/services/socketService.ts
import { io, Socket } from 'socket.io-client';
import store from '@/store';

export interface SocketNotificationData {
  type:
    | 'new_message'
    | 'user_follow'
    | 'product_follow'
    | 'product_expired'
    | 'new_rating';
  message?: any;
  sender?: {
    id: number;
    username: string;
  };
  product?: {
    id: number;
    name: string;
    slug: string;
  };
  follower?: {
    id: number;
    username: string;
  };
  rater?: {
    id: number;
    username: string;
  };
  rating?: {
    type: string;
    isLike: boolean;
    comment?: string;
  };
  timestamp: string;
}

class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private connectionPromise: Promise<void> | null = null;

  // Socket URL sa fallback
  private readonly socketUrl =
    process.env.VUE_APP_SOCKET_URL || 'ws://localhost:3001';

  constructor() {
    console.log('[SocketService] Service initialized');
    console.log('[SocketService] Socket URL:', this.socketUrl);
  }

  async connect(): Promise<void> {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (this.socket?.connected) {
      console.log('[SocketService] Already connected');
      return Promise.resolve();
    }

    this.connectionPromise = this._connect();
    try {
      await this.connectionPromise;
    } finally {
      this.connectionPromise = null;
    }
  }

  private async _connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log('[SocketService] Attempting to connect to:', this.socketUrl);

      // ISPRAVKA: Koristi istu logiku kao debug tool
      const token =
        localStorage.getItem('token') || (store.state as any).auth?.token;

      if (!token) {
        console.error('[SocketService] No auth token available');
        console.log(
          '[SocketService] LocalStorage token:',
          !!localStorage.getItem('token')
        );
        console.log(
          '[SocketService] Store token:',
          !!(store.state as any).auth?.token
        );

        store.dispatch('notifications/setConnectionStatus', {
          isConnected: false,
          error: 'Nema auth token-a',
        });
        reject(new Error('No auth token'));
        return;
      }

      console.log(
        '[SocketService] Using token:',
        token.substring(0, 30) + '...'
      );

      if (this.socket) {
        console.log('[SocketService] Disconnecting existing socket');
        this.socket.disconnect();
      }

      // Koristimo dinamičku URL umesto hardkodovane
      this.socket = io(this.socketUrl, {
        query: { token },
        transports: ['websocket', 'polling'],
        timeout: 10000,
        forceNew: true,
        autoConnect: true,
      });

      this.socket.on('connect', () => {
        console.log(
          '[SocketService] ✅ Connected successfully to:',
          this.socketUrl,
          'socket ID:',
          this.socket?.id
        );
        this.reconnectAttempts = 0;
        store.dispatch('notifications/setConnectionStatus', {
          isConnected: true,
          error: null,
        });
        resolve();
      });

      this.socket.on('authenticated', (data: any) => {
        console.log('[SocketService] ✅ Authenticated:', data);
      });

      this.socket.on('connect_error', (error: any) => {
        console.error(
          '[SocketService] ❌ Connection error to:',
          this.socketUrl,
          error
        );
        console.log('[SocketService] Error details:', {
          message: error.message,
          description: error.description,
          context: error.context,
          type: error.type,
        });

        store.dispatch('notifications/setConnectionStatus', {
          isConnected: false,
          error: `Greška konekcije: ${error.message}`,
        });
        this._handleReconnect();
        reject(error);
      });

      this.socket.on('disconnect', (reason: any) => {
        console.log(
          '[SocketService] ❌ Disconnected from:',
          this.socketUrl,
          reason
        );
        store.dispatch('notifications/setConnectionStatus', {
          isConnected: false,
          error:
            reason === 'io server disconnect'
              ? 'Server je prekinuo konekciju'
              : null,
        });

        if (reason === 'io server disconnect') {
          this._handleReconnect();
        }
      });

      this._setupNotificationHandlers();
    });
  }

  private _setupNotificationHandlers(): void {
    if (!this.socket) return;

    // ISPRAVKA: Sluša prave socket event nazive koje backend šalje
    this.socket.on('newMessage', (data: SocketNotificationData) => {
      console.log('[SocketService] 📩 New message notification:', data);
      store.dispatch('notifications/addNotification', {
        ...data,
        type: 'new_message',
      });
    });

    this.socket.on('product_follow', (data: SocketNotificationData) => {
      console.log('[SocketService] 📦 Product follow notification:', data);
      store.dispatch('notifications/addNotification', {
        ...data,
        type: 'product_follow',
      });
    });

    this.socket.on('user_follow', (data: SocketNotificationData) => {
      console.log('[SocketService] 👤 User follow notification:', data);
      store.dispatch('notifications/addNotification', {
        ...data,
        type: 'user_follow',
      });
    });

    this.socket.on('product_expired', (data: SocketNotificationData) => {
      console.log('[SocketService] ⏰ Product expired notification:', data);
      store.dispatch('notifications/addNotification', {
        ...data,
        type: 'product_expired',
      });
    });

    this.socket.on('new_rating', (data: SocketNotificationData) => {
      console.log('[SocketService] ⭐ New rating notification:', data);
      store.dispatch('notifications/addNotification', {
        ...data,
        type: 'new_rating',
      });
    });

    this.socket.on('pong', (data: any) => {
      console.log('[SocketService] 🏓 Received pong:', data);
    });
  }

  private _handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SocketService] ❌ Max reconnection attempts reached');
      store.dispatch('notifications/setConnectionStatus', {
        isConnected: false,
        error: 'Prekoračen broj pokušaja reconnect-a',
      });
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(
      `[SocketService] 🔄 Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    );

    setTimeout(() => {
      this.connect().catch((error) => {
        console.error('[SocketService] Reconnection failed:', error);
      });
    }, delay);
  }

  disconnect(): void {
    console.log('[SocketService] Disconnecting from:', this.socketUrl);

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.reconnectAttempts = 0;
    this.connectionPromise = null;

    store.dispatch('notifications/setConnectionStatus', {
      isConnected: false,
      error: null,
    });
  }

  sendPing(): void {
    if (this.socket?.connected) {
      console.log('[SocketService] 🏓 Sending ping to:', this.socketUrl);
      this.socket.emit('ping');
    } else {
      console.warn(
        '[SocketService] Cannot send ping - not connected to:',
        this.socketUrl
      );
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  async forceReconnect(): Promise<void> {
    console.log('[SocketService] Force reconnecting to:', this.socketUrl);
    this.disconnect();
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await this.connect();
  }
}

export const socketService = new SocketService();
export default socketService;
